import BaseAPIService from './BaseAPIService'
import endpoints from '@/config/endpoints'
import {
  Lihe,
  LiheResponse,
  Service,
  ServiceResponse,
  ServiceLocale,
} from './CatalogService.interface'

// Types
import { Locale } from '@/types'

// Store
import store from '@/store'

class CatalogService extends BaseAPIService {
  constructor() {
    super(endpoints.catalog) // Use "catalog" key from endpoints
  }

  /**
   * Fetch Lihe details by ID.
   */
  public async getLihe(liheId: number): Promise<Partial<Lihe>> {
    try {
      const url = `/lihe/one/${liheId}/full`
      const response = await this.apiClient.get<LiheResponse>(url, {
        headers: this.getDefaultHeaders(),
      })

      const data = response.data.message.lihe // Access the nested 'lihe' property under 'message'

      // Transform the `prests` array to extract `prestId` and `accomodationName`
      const transformedPrests = data.prests.map(prest => ({
        prestId: prest.prestId,
        accomodationName:
          prest.prestsTranslation[0]?.accomodationName,
      }))

      // Extract the required fields
      return {
        liheName: data.name,
        stationName: data.station.stationNameFr,
        stationId: data.stationId,
        liheId: data.liheId,
        liheImages: data.liheImages?.length > 0 ? [data.liheImages[0]] : [],
        prests: transformedPrests,
      }
    } catch (error) {
      console.error('Error fetching Lihe details:', error)
      throw error
    }
  }

  /**
   * Fetch multiple Services by IDs.
   * @param serviceIds - Array of service IDs to fetch
   */ //
  public async getServices(serviceIds: number[]): Promise<Partial<Service>[]> {
    try {
      const url = '/services/search'

      // Retrieve the locale directly from the store
      const formattedLocale = store.getters.getLocale.split(
        '-'
      )[0] as ServiceLocale

      const response = await this.apiClient.get<ServiceResponse>(url, {
        params: {
          id: JSON.stringify(serviceIds),
          pageSize: 10,
          page: 1,
          local: formattedLocale,
        },
        headers: this.getDefaultHeaders(),
      })
      return response.data.message.service.map(service => {
        let translation: Partial<Service> = {}
        if (
          Array.isArray(service.serviceTranslation) &&
          service.serviceTranslation.length > 0
        ) {
          translation = service.serviceTranslation[0]
        }

        return {
          id: service.id,
          faseId: service.faseId,
          code: service.code,
          providerId: service.providerId,
          providerName: service.providerName,
          nameCommercial: translation.nameCommercial || '', 
          name: translation.name || '', 
        }
      })
    } catch (error) {
      console.error('Error fetching Services:', error)
      throw error
    }
  }

  /**
   * Overrides the default headers with additional customizations.
   * This method removes the `locale` property from the default headers,
   * retrieves the current locale from the store, and adds an `Accept-Language` header
   * based on the locale.
   *
   * @protected
   * @override
   * @returns {Record<string, string>} The customized headers object.
   */
  protected override getDefaultHeaders(): Record<string, string> {
    // Call the parent method to get the default headers
    const defaultHeaders = super.getDefaultHeaders()

    // Remove the 'locale' property
    delete defaultHeaders.locale

    // Add or overwrite other headers
    return {
      ...defaultHeaders,
      'Accept-Language': this.getLocale(), // Add or overwrite 'Accept-Language'
    }
  }

  /**
   * Retrieves the current locale from the store and formats it by removing
   * the "-CSE" suffix, if present.
   *
   * @protected
   * @override
   * @returns {Locale} The formatted locale string.
   */
  protected override getLocale(): Locale {
    // Get the current locale from the store
    const locale: Locale = store.getters.getLocale

    // Return the formatted locale
    return locale.replace('-CSE', '') as Locale
  }
}

export default CatalogService
